import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CreatorNavbar from '../components/dashboard/CreatorNavbar';
import CreatorPageHeader from '../components/dashboard/CreatorPageHeader';
import Footer from '../components/Footer';
import { sponsorshipAPI } from '../api';

const CreatorSponsorshipsPage = () => {
  const [sponsorships, setSponsorships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null); // null = all statuses
  const navigate = useNavigate();

  useEffect(() => {
    fetchSponsorships();
  }, [filter]);

  const fetchSponsorships = async () => {
    try {
      setLoading(true);
      // Backend enforces creator sees only their own sponsorships
      const data = await sponsorshipAPI.getAllSponsorships(filter);
      setSponsorships(data);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const calculateProgress = (funded, required) => {
    return Math.min((funded / required) * 100, 100);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'FULLY_FUNDED':
        return 'bg-green-100 text-green-800';
      case 'PUBLISHED':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <CreatorNavbar />

      <main className="flex-grow container mx-auto px-4 py-8 mt-20">
        <div className="max-w-6xl mx-auto">
          <CreatorPageHeader
            kicker="Monetization workspace"
            title="My Sponsorships"
            description="Manage and track the progress of your sponsorship campaigns."
            className="mb-8"
            actions={
              <button
                onClick={() => navigate('/sponsorships/new')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Sponsorship
              </button>
            }
          />

          {/* Filter Tabs */}
          <div className="mb-6 flex gap-3 flex-wrap">
            {[
              { label: 'All', value: null },
              { label: 'Draft', value: 'DRAFT' },
              { label: 'Published', value: 'PUBLISHED' },
              { label: 'Fully Funded', value: 'FULLY_FUNDED' },
            ].map(({ label, value }) => (
              <button
                key={label}
                onClick={() => setFilter(value)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  filter === value
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading your sponsorships...</p>
            </div>
          ) : sponsorships.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-lg shadow">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Sponsorships Yet</h3>
              <p className="text-gray-600 mb-6">
                {filter ? `No ${filter.toLowerCase().replace('_', ' ')} sponsorships found.` : 'Create your first sponsorship to raise funds from brands.'}
              </p>
              {!filter && (
                <button
                  onClick={() => navigate('/sponsorships/new')}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition"
                >
                  Create Your First Sponsorship
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sponsorships.map((sponsorship) => (
                <div
                  key={sponsorship.id}
                  className="bg-white rounded-lg shadow hover:shadow-lg transition cursor-pointer"
                  onClick={() => navigate(`/dashboard/creator/sponsorships/${sponsorship.id}`)}
                >
                  <div className="p-6">
                    {/* Status Badge */}
                    <div className="mb-3 flex items-center justify-between">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusStyle(sponsorship.status)}`}>
                        {sponsorship.status.replace('_', ' ')}
                      </span>
                      {sponsorship.status === 'DRAFT' && (
                        <span className="text-xs text-orange-600 font-medium">Unpublished</span>
                      )}
                    </div>

                    {/* Project Overview */}
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2">
                      {sponsorship.projectOverview}
                    </h3>

                    {/* Interest Badge */}
                    {sponsorship.interest && (
                      <div className="mb-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                          {sponsorship.interest}
                        </span>
                      </div>
                    )}

                    {/* Funding Progress */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(sponsorship.amountFunded)}
                        </span>
                        <span className="text-gray-600">
                          of {formatCurrency(sponsorship.amountRequired)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{
                            width: `${calculateProgress(sponsorship.amountFunded, sponsorship.amountRequired)}%`
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{sponsorship.investmentCount || 0} investors</span>
                      <span>{formatCurrency(sponsorship.remainingAmount)} remaining</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CreatorSponsorshipsPage;
